// Statistiques sécurisées du Directeur (propriétaire OU agence).
//
// Source de vérité : la fonction RPC `stats_dashboard_dg()` (SECURITY DEFINER),
// qui calcule le périmètre à partir de l'utilisateur authentifié — jamais d'une
// valeur fournie par le navigateur. Aucune statistique n'est recalculée côté
// frontend, et aucune donnée fictive n'est utilisée.

import { useEffect, useState } from "react";
import { supabase } from "@/store/shared/supabase";
import { describeSupabaseError } from "@/store/shared/dataErrors";
import { useSession } from "@/session/SessionProvider";

export interface DgStats {
  role: string;
  immeubles: number;
  biens: number;
  biens_occupes: number;
  biens_disponibles: number;
  locataires: number;
  contrats_actifs: number;
  loyers_a_payer: number;
  loyers_en_retard: number;
}

const EMPTY_STATS: DgStats = {
  role: "",
  immeubles: 0,
  biens: 0,
  biens_occupes: 0,
  biens_disponibles: 0,
  locataires: 0,
  contrats_actifs: 0,
  loyers_a_payer: 0,
  loyers_en_retard: 0,
};

export interface UseDgStatsResult {
  stats: DgStats;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

export function useDgStats(): UseDgStatsResult {
  const { session } = useSession();
  const [stats, setStats] = useState<DgStats>(EMPTY_STATS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!session) {
      setStats(EMPTY_STATS);
      return;
    }
    let active = true;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const { data, error: rpcError } = await supabase.rpc("stats_dashboard_dg");
        if (rpcError) throw rpcError;
        if (active) setStats({ ...EMPTY_STATS, ...((data as Partial<DgStats> | null) ?? {}) });
      } catch (caught) {
        if (active) {
          setError(describeSupabaseError(caught));
          console.error("[Dashboard] stats_dashboard_dg échoué", caught);
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [session, nonce]);

  return { stats, loading, error, reload: () => setNonce((value) => value + 1) };
}